import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetClose, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Progress } from "@/components/ui/progress";
import {
  AlertTriangle,
  ArrowLeft,
  BarChart3,
  Bell,
  BookOpen,
  Building2,
  CheckCircle,
  Download,
  ExternalLink,
  FileText,
  KeyRound,
  Landmark,
  LayoutDashboard,
  Library,
  LogOut,
  Menu,
  MessageCircle,
  Home,
  Settings,
  ServerCog,
  ShieldCheck,
  ShieldAlert,
  ShoppingBag,
  ShoppingCart,
  TrendingUp,
  UserCog,
  Wallet,
  Users,
  Zap,
} from "lucide-react";
import { PARTNER_LEVELS } from "@/utils/constants";
import { useCart } from "@/contexts/CartContext";
import { notificationsAPI, profileAPI } from "@/services/api";
import { ADMIN_RANKS, canAccessCashPages, getAdminRanks, hasAdminRank, hasExplicitAdminRank, hasFullAdminRank, isFounderAccount } from "@/utils/adminRanks";

export default function DashboardLayout({ user, onLogout, children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { totalItems } = useCart();
  const [notifications, setNotifications] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);
  const [profilePhotoVersion, setProfilePhotoVersion] = useState("");
  const adminRanks = getAdminRanks(user);

  const isActive = (path) => location.pathname === path;

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let cancelled = false;
    profileAPI.getMe().then((res) => {
      if (!cancelled) {
        const kyc = res.data?.kyc || {};
        const profileUser = res.data?.user || {};
        setProfilePhotoVersion(profileUser.profile_photo_updated_at || profileUser.profile_photo_path || kyc.profile_photo_path || "");
      }
    }).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const loadNotifications = async () => {
    try {
      const res = await notificationsAPI.list();
      setNotifications(Array.isArray(res.data) ? res.data : []);
    } catch (e) {}
  };

  const unreadCount = (Array.isArray(notifications) ? notifications : []).filter((n) => !n.is_read).length;
  const canGoBack = location.pathname !== "/dashboard";

  const markAllRead = async () => {
    try {
      await notificationsAPI.markAllRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    } catch (e) {}
  };

  const markNotificationRead = async (notification) => {
    if (!notification?.id || notification.is_read) return;
    try {
      await notificationsAPI.markRead(notification.id);
      setNotifications((prev) => prev.map((item) => (
        item.id === notification.id ? { ...item, is_read: true } : item
      )));
    } catch (e) {}
  };

  const navItems = [
    { path: "/", label: "Accueil MMJ", icon: Home, roles: ["admin", "partner", "buyer"] },
    { path: "/dashboard", label: "Tableau de bord", icon: LayoutDashboard, roles: ["admin", "partner", "buyer"] },
    { path: "/catalogue", label: "Catalogue ARTICO", icon: BookOpen, roles: ["partner", "buyer"] },
    { path: "/cart", label: "Panier", icon: ShoppingBag, roles: ["partner", "buyer"], badge: totalItems > 0 ? totalItems : null },
    { path: "/library", label: "Ma Bibliotheque", icon: Library, roles: ["partner", "buyer", "admin"] },
    { path: "/messages", label: "Messagerie", icon: MessageCircle, roles: ["admin", "partner", "buyer"], adminRanks: [ADMIN_RANKS.GP] },
    { path: "/orders", label: "Commandes", icon: ShoppingCart, roles: ["admin", "partner", "buyer"], adminRanks: [ADMIN_RANKS.C, ADMIN_RANKS.CAISSE] },
    { path: "/products/submit", label: "Soumettre un produit", icon: FileText, roles: ["partner"] },
    { path: "/admin/catalogue-libre/submit", label: "Déposer une oeuvre libre", icon: FileText, roles: ["admin"], adminRanks: [ADMIN_RANKS.CL], explicitRank: ADMIN_RANKS.CL },
    { path: "/team", label: "Mon equipe", icon: Users, roles: ["partner"] },
    { path: "/bonuses", label: "Mes bonus", icon: TrendingUp, roles: ["partner"] },
    { path: "/verification", label: "Verification ID", icon: ShieldCheck, roles: ["partner"] },
    { path: "/analytics", label: "Analytics", icon: BarChart3, roles: ["admin", "partner"], adminRanks: [ADMIN_RANKS.FULL] },
    { path: "/admin/products", label: "Validation produits", icon: CheckCircle, roles: ["admin"], adminRanks: [ADMIN_RANKS.C] },
    { path: "/admin/activations", label: "Gestion activations", icon: Zap, roles: ["admin"], adminRanks: [ADMIN_RANKS.B, ADMIN_RANKS.CAISSE] },
    { path: "/admin/withdrawals", label: "Paiements bonus", icon: Wallet, roles: ["admin"], adminRanks: [ADMIN_RANKS.B, ADMIN_RANKS.CAISSE] },
    { path: "/admin/kyc", label: "Verification partenaires", icon: ShieldCheck, roles: ["admin"], adminRanks: [ADMIN_RANKS.A] },
    { path: "/admin/platform-settings", label: "Infos ARTICO-MMJ", icon: Building2, roles: ["admin"], adminRanks: [ADMIN_RANKS.GP] },
    { path: "/admin/moderation", label: "Moderation", icon: ShieldAlert, roles: ["admin"], adminRanks: [ADMIN_RANKS.GP, ADMIN_RANKS.FULL] },
    { path: "/admin/founder", label: "Fondateur", icon: Landmark, roles: ["admin"], access: "founder" },
    { path: "/admin/artico-cash", label: "Journal de la Caisse", icon: Landmark, roles: ["admin"], access: "cash" },
    { path: "/admin/users", label: "Gestion utilisateurs", icon: Users, roles: ["admin"], adminRanks: [ADMIN_RANKS.GP, ADMIN_RANKS.CAISSE] },
    { path: "/admin/system", label: "Systeme", icon: ServerCog, roles: ["admin"], adminRanks: [ADMIN_RANKS.FULL] },
  ];

  const filteredNav = navItems.filter((item) => {
    if (!item.roles.includes(user.role)) return false;
    if (user.role !== "admin") return true;
    if (item.access === "founder") return isFounderAccount(user);
    if (item.access === "cash") return canAccessCashPages(user);
    if (!item.adminRanks) return true;
    if (item.explicitRank) return hasExplicitAdminRank(user, item.explicitRank);
    if (item.adminRanks.includes(ADMIN_RANKS.FULL)) return hasFullAdminRank(user);
    return hasAdminRank(user, ...item.adminRanks);
  });
  const bottomNavItems = getMobileBottomNavItems(user, filteredNav);
  const hasBottomNav = bottomNavItems.length > 0;

  return (
    <div className="mmj-app-viewport bg-[#FCFBF8] lg:flex">
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 border-r border-[#E6E4E0] bg-white lg:flex">
        <SidebarContent
          user={user}
          profilePhotoVersion={profilePhotoVersion}
          adminRanks={adminRanks}
          filteredNav={filteredNav}
          isActive={isActive}
          onLogout={onLogout}
        />
      </aside>

      <main className="flex min-h-[100svh] min-w-0 flex-1 flex-col">
        <header className="mmj-safe-top sticky top-0 z-40 border-b border-[#E6E4E0] bg-[#FCFBF8]/95 px-3 pb-2 pt-3 backdrop-blur sm:px-4 lg:px-8 lg:py-4">
          <div className="mx-auto flex w-full max-w-[1400px] items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-3">
              {canGoBack && (
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[#E6E4E0] bg-white text-[#2A4B46] shadow-sm transition-colors hover:bg-[#F3EFE9]"
                  aria-label="Retour"
                  data-testid="mobile-back-button"
                >
                  <ArrowLeft size={20} />
                </button>
              )}
              <MobileNavSheet
                user={user}
                adminRanks={adminRanks}
                filteredNav={filteredNav}
                isActive={isActive}
                onLogout={onLogout}
                profilePhotoVersion={profilePhotoVersion}
              />
              <div className="min-w-0 lg:hidden">
                <p className="truncate text-base font-extrabold text-[#2A4B46] font-['Outfit']">MMJ</p>
                <p className="truncate text-xs text-[#5C625F]">{currentPageLabel(filteredNav, location.pathname)}</p>
              </div>
              <div className="hidden min-w-0 lg:block">
                <p className="truncate text-lg font-extrabold text-[#2A4B46] font-['Outfit']">{currentPageLabel(filteredNav, location.pathname)}</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <NotificationsMenu
                notifications={notifications}
                showNotifs={showNotifs}
                setShowNotifs={setShowNotifs}
                unreadCount={unreadCount}
                markAllRead={markAllRead}
                markNotificationRead={markNotificationRead}
                user={user}
                navigate={navigate}
              />
              <ProfileAccountDrawer user={user} adminRanks={adminRanks} onLogout={onLogout} profilePhotoVersion={profilePhotoVersion}>
                <button
                  className="flex h-10 w-10 overflow-hidden items-center justify-center rounded-full bg-[#2A4B46] text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#1f3a36]"
                  data-testid="top-profile-avatar-button"
                  aria-label="Ouvrir le profil"
                >
                  <ProfileAvatar user={user} version={profilePhotoVersion} />
                </button>
              </ProfileAccountDrawer>
            </div>
          </div>
        </header>

        <div className={`mx-auto w-full max-w-[1400px] flex-1 px-4 pt-4 sm:px-6 lg:px-8 lg:pt-6 ${hasBottomNav ? "pb-8" : "pb-20 lg:pb-12"} mmj-safe-bottom`}>
          {children}
          <footer className="mt-8 border-t border-[#E6E4E0] pt-4 text-center text-xs text-[#6D7470]">
            <Link to="/legal" className="font-semibold text-[#2A4B46] hover:underline">
              Confidentialite et conditions d'utilisation
            </Link>
          </footer>
        </div>
        {hasBottomNav && <MobileBottomNav items={bottomNavItems} isActive={isActive} />}
      </main>
    </div>
  );
}

function getMobileBottomNavItems(user, filteredNav) {
  if (user.role === "admin") return [];
  const preferred = user.role === "partner"
    ? ["/dashboard", "/catalogue", "/library", "/cart", "/bonuses"]
    : ["/dashboard", "/catalogue", "/library", "/cart", "/orders"];
  return preferred
    .map((path) => filteredNav.find((item) => item.path === path))
    .filter(Boolean)
    .slice(0, 5);
}

function MobileBottomNav({ items, isActive }) {
  return (
    <nav className="mmj-safe-bottom sticky inset-x-0 bottom-0 z-40 border-t border-[#E6E4E0] bg-white/95 px-2 pb-2 pt-2 shadow-[0_-10px_30px_rgba(42,75,70,0.08)] backdrop-blur lg:hidden" data-testid="mobile-bottom-nav">
      <div className="mx-auto grid max-w-md grid-cols-5 gap-1">
        {items.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          return (
            <Link
              key={item.path}
              to={item.path}
              className={
                active
                  ? "flex min-h-[58px] flex-col items-center justify-center gap-1 rounded-xl bg-[#E7EFEC] px-1 text-center text-[11px] font-bold text-[#2A4B46]"
                  : "flex min-h-[58px] flex-col items-center justify-center gap-1 rounded-xl px-1 text-center text-[11px] font-semibold text-[#6D7470] transition-colors hover:bg-[#F3EFE9] hover:text-[#2A4B46]"
              }
            >
              <span className="relative">
                <Icon size={20} />
                {item.badge && (
                  <span className="absolute -right-2 -top-2 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#D4A373] px-1 text-[10px] text-white">
                    {item.badge}
                  </span>
                )}
              </span>
              <span className="max-w-full truncate">{shortMobileLabel(item.label)}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}

function SidebarContent({ user, adminRanks, filteredNav, isActive, onLogout, closeOnNavigate = false, profilePhotoVersion = "" }) {
  return (
    <div className="flex h-full w-full flex-col">
      <div className="border-b border-[#E6E4E0] p-5">
        <h1 className="text-2xl font-extrabold text-[#2A4B46] font-['Outfit']" data-testid="dashboard-logo">
          MMJ
        </h1>
        <p className="mt-1 text-sm text-[#5C625F]">Maison d'edition</p>
      </div>

      <div className="border-b border-[#E6E4E0] p-5">
        <div className="mb-3 flex items-center gap-3">
          <ProfileAccountDrawer user={user} adminRanks={adminRanks} onLogout={onLogout} profilePhotoVersion={profilePhotoVersion}>
            <button
              className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#2A4B46] text-sm font-semibold text-white transition-colors hover:bg-[#1f3a36]"
              data-testid="profile-avatar-button"
              aria-label="Ouvrir le profil"
            >
              <ProfileAvatar user={user} version={profilePhotoVersion} />
            </button>
          </ProfileAccountDrawer>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-[#1A1D1C]" data-testid="user-name">{user.full_name}</p>
            <p className="truncate text-xs text-[#5C625F]">{user.email}</p>
          </div>
        </div>
        <RoleBadge user={user} adminRanks={adminRanks} />
      </div>

      <nav className="min-h-0 flex-1 overflow-y-auto p-3" data-testid="dashboard-nav">
        <ul className="space-y-1">
          {filteredNav.map((item) => {
            const Icon = item.icon;
            const content = (
              <Link
                to={item.path}
                data-testid={`nav-${item.path.replace("/", "-")}`}
                className={
                  isActive(item.path)
                    ? "flex min-h-11 items-center gap-3 rounded-xl bg-[#2A4B46] px-3 py-2.5 text-sm font-semibold text-white"
                    : "flex min-h-11 items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-[#5C625F] transition-colors hover:bg-[#F3EFE9] hover:text-[#2A4B46]"
                }
              >
                <Icon size={19} />
                <span className="min-w-0 flex-1 truncate">{item.label}</span>
                {item.badge && (
                  <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-[#D4A373] px-1.5 text-xs text-white">
                    {item.badge}
                  </span>
                )}
              </Link>
            );
            return <li key={item.path}>{closeOnNavigate ? <SheetClose asChild>{content}</SheetClose> : content}</li>;
          })}
        </ul>
      </nav>

      <div className="border-t border-[#E6E4E0] p-3">
        <Link
          to="/legal"
          className="mb-2 flex min-h-9 items-center gap-3 rounded-xl px-3 py-2 text-xs font-semibold text-[#5C625F] transition-colors hover:bg-[#F3EFE9] hover:text-[#2A4B46]"
          data-testid="dashboard-legal-link"
        >
          <FileText size={16} />
          <span>Confidentialite et conditions</span>
        </Link>
        <Button
          variant="ghost"
          className="min-h-11 w-full justify-start gap-3 rounded-xl text-[#5C625F] hover:bg-[#F3EFE9] hover:text-[#2A4B46]"
          data-testid="logout-button"
          onClick={onLogout}
        >
          <LogOut size={20} />
          <span>Deconnexion</span>
        </Button>
      </div>
    </div>
  );
}

function MobileNavSheet({ user, adminRanks, filteredNav, isActive, onLogout, profilePhotoVersion }) {
  return (
    <Sheet>
      <SheetTrigger asChild>
        <button
          type="button"
          className="flex h-10 w-10 items-center justify-center rounded-xl border border-[#E6E4E0] bg-white text-[#2A4B46] shadow-sm lg:hidden"
          aria-label="Ouvrir le menu"
          data-testid="mobile-nav-trigger"
        >
          <Menu size={21} />
        </button>
      </SheetTrigger>
      <SheetContent side="left" className="w-[88vw] max-w-[340px] border-[#E6E4E0] bg-white p-0">
        <SheetHeader className="sr-only">
          <SheetTitle>Navigation</SheetTitle>
        </SheetHeader>
        <SidebarContent
          user={user}
          adminRanks={adminRanks}
          filteredNav={filteredNav}
          isActive={isActive}
          onLogout={onLogout}
          closeOnNavigate
          profilePhotoVersion={profilePhotoVersion}
        />
      </SheetContent>
    </Sheet>
  );
}

function NotificationsMenu({ notifications, showNotifs, setShowNotifs, unreadCount, markAllRead, markNotificationRead, user, navigate }) {
  const safeNotifications = Array.isArray(notifications) ? notifications : [];
  const menuRef = useRef(null);

  useEffect(() => {
    if (!showNotifs) return undefined;
    const closeOnOutsideClick = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowNotifs(false);
      }
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("touchstart", closeOnOutsideClick);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("touchstart", closeOnOutsideClick);
    };
  }, [setShowNotifs, showNotifs]);

  const handleNotificationClick = async (notification) => {
    const target = getNotificationTarget(notification, user);
    await markNotificationRead(notification);
    if (target) {
      setShowNotifs(false);
      navigate(target);
    }
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setShowNotifs(!showNotifs)}
        className="relative flex h-10 w-10 items-center justify-center rounded-xl text-[#5C625F] transition-colors hover:bg-[#F3EFE9]"
        data-testid="notifications-bell"
        aria-label="Notifications"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="absolute right-1 top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-xs text-white">
            {unreadCount}
          </span>
        )}
      </button>

      {showNotifs && (
        <div className="fixed left-3 right-3 top-16 z-50 max-h-[calc(100vh-5rem)] w-auto overflow-y-auto rounded-xl border border-[#E6E4E0] bg-white shadow-xl sm:absolute sm:left-auto sm:right-0 sm:top-12 sm:w-[min(24rem,calc(100vw-2rem))]" data-testid="notifications-panel">
          <div className="flex items-center justify-between gap-3 border-b border-[#E6E4E0] p-3">
            <h4 className="min-w-0 text-sm font-semibold text-[#2A4B46]">Notifications</h4>
            {unreadCount > 0 && (
              <button onClick={markAllRead} className="shrink-0 text-xs font-semibold text-[#D4A373] hover:underline">
                Tout marquer lu
              </button>
            )}
          </div>
          {safeNotifications.length === 0 ? (
            <div className="p-4 text-center text-sm text-[#5C625F]">Aucune notification</div>
          ) : (
            safeNotifications.slice(0, 15).map((notif) => {
              const target = getNotificationTarget(notif, user);
              return (
              <button
                type="button"
                key={notif.id}
                onClick={() => handleNotificationClick(notif)}
                className={`block w-full border-b border-[#E6E4E0] p-3 text-left transition-colors last:border-b-0 hover:bg-[#F3EFE9] ${!notif.is_read ? "bg-blue-50/50" : ""}`}
              >
                <div className="flex min-w-0 items-start justify-between gap-3">
                  <p className="min-w-0 break-words text-sm font-semibold text-[#2A4B46]">{notif.title}</p>
                  {target && <ExternalLink size={14} className="mt-0.5 shrink-0 text-[#D4A373]" />}
                </div>
                <p className="mt-1 break-words text-xs text-[#5C625F]">{notif.message}</p>
                <p className="mt-1 text-xs text-[#9CA3AF]">
                  {new Date(notif.created_at).toLocaleDateString("fr-FR")}
                </p>
              </button>
            );})
          )}
        </div>
      )}
    </div>
  );
}

function RoleBadge({ user, adminRanks }) {
  if (user.role === "partner" && user.partner_level) {
    return (
      <Badge className={`${PARTNER_LEVELS[user.partner_level]?.color} w-full justify-center rounded-lg py-1`} data-testid="user-level-badge">
        {PARTNER_LEVELS[user.partner_level]?.label}
      </Badge>
    );
  }

  if (user.role === "admin") {
    return (
      <Badge className="w-full justify-center rounded-lg bg-red-100 py-1 text-red-800">
        Administrateur {adminRanks.length ? `(${adminRanks.join(", ")})` : ""}
      </Badge>
    );
  }

  return <Badge className="w-full justify-center rounded-lg bg-blue-100 py-1 text-blue-800">Acheteur</Badge>;
}

function currentPageLabel(navItems, pathname) {
  return navItems.find((item) => item.path === pathname)?.label || "Espace ARTICO";
}

function shortMobileLabel(label) {
  const labels = {
    "Tableau de bord": "Accueil",
    "Catalogue ARTICO": "Catalogue",
    "Ma Bibliotheque": "Bibliotheque",
    "Mes bonus": "Bonus",
    "Commandes": "Commandes",
    "Panier": "Panier",
  };
  return labels[label] || label;
}

function getNotificationTarget(notification, user) {
  if (!notification) return null;
  if (notification.action_url || notification.actionUrl) return notification.action_url || notification.actionUrl;

  const type = String(notification.notif_type || notification.type || "").toLowerCase();
  const text = `${notification.title || ""} ${notification.message || ""}`.toLowerCase();
  const isAdmin = user?.role === "admin";

  if (type.includes("kyc") || text.includes("kyc") || text.includes("identit")) {
    return isAdmin ? "/admin/kyc" : "/verification";
  }

  if (type.includes("activation") || text.includes("activation")) {
    return isAdmin ? "/admin/activations" : "/cart";
  }

  if (type.includes("purchase") || type.includes("order") || text.includes("commande") || text.includes("paiement") || text.includes("preuve")) {
    return "/orders";
  }

  if (type.includes("bonus") || text.includes("bonus")) {
    return isAdmin ? "/analytics" : "/bonuses";
  }

  if (type.includes("product") || text.includes("produit") || text.includes("oeuvre") || text.includes("ouvrage")) {
    return isAdmin ? "/admin/products" : "/products/submit";
  }

  if (type.includes("library") || text.includes("biblioth")) {
    return "/library";
  }

  if (text.includes("mot de passe") || text.includes("profil") || text.includes("compte")) {
    return "/profile";
  }

  return null;
}

function getInitials(name = "") {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return (parts[0]?.[0] || "U").toUpperCase() + (parts[1]?.[0] || "").toUpperCase();
}

function ProfileAvatar({ user, version = "" }) {
  const [failed, setFailed] = useState(false);
  if (failed) return getInitials(user?.full_name);
  return (
    <img
      src={profileAPI.getPhotoUrl(version)}
      alt="Photo de profil"
      className="h-full w-full object-cover"
      onError={() => setFailed(true)}
    />
  );
}

function getAscensionProgress(user) {
  const points = Number(user?.points_ascension || 0);
  const level = user?.partner_level || "P-00";
  const nextByLevel = {
    "P-00": { threshold: 12, label: "P-12" },
    "P-12": { threshold: 24, label: "P-24" },
    "P-24": { threshold: 36, label: "P-36" },
    "P-36": { threshold: 50, label: "E-50" },
    "E-50": null,
  };
  const next = nextByLevel[level] || nextByLevel["P-00"];

  if (!next) {
    return { points, percent: 100, label: `${points} points - niveau Etoile atteint` };
  }

  return {
    points,
    percent: Math.min(100, Math.max(0, (points / next.threshold) * 100)),
    label: `${points} / ${next.threshold} points pour ${next.label}`,
  };
}

function ProfileAccountDrawer({ user, adminRanks, onLogout, children, profilePhotoVersion = "" }) {
  const [open, setOpen] = useState(false);
  const [profile, setProfile] = useState(null);
  const isFullAdmin = hasFullAdminRank(user);
  const displayUser = profile?.user || user;
  const progress = getAscensionProgress(displayUser);
  const levelLabel = displayUser.role === "partner"
    ? (displayUser.partner_level || "P-00")
    : displayUser.role === "admin"
      ? `Admin ${adminRanks.length ? adminRanks.join(", ") : ""}`.trim()
      : "Acheteur";
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    Promise.allSettled([
      profileAPI.getMe(),
    ]).then(([profileRes]) => {
      if (cancelled) return;
      if (profileRes.status === "fulfilled") setProfile(profileRes.value.data);
    });
    return () => {
      cancelled = true;
    };
  }, [open, displayUser.role]);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>{children}</SheetTrigger>
      <SheetContent side="right" className="w-full overflow-y-auto border-[#E6E4E0] bg-white sm:max-w-md" data-testid="profile-drawer">
        <SheetHeader>
          <SheetTitle className="text-[#2A4B46]">Compte ARTICO</SheetTitle>
        </SheetHeader>

        <div className="mt-6 rounded-[28px] border border-[#E6E4E0] bg-white p-5 shadow-sm">
          <div className="flex flex-col items-center text-center">
            <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-full bg-[#2A4B46] text-2xl font-bold text-white ring-4 ring-[#F3EFE9]">
              <ProfileAvatar user={displayUser} version={displayUser.profile_photo_updated_at || displayUser.profile_photo_path || profile?.kyc?.profile_photo_path || profilePhotoVersion} />
            </div>
            <p className="mt-4 text-xl font-semibold text-[#1A1D1C]">{displayUser.full_name}</p>
            <p className="break-all text-sm text-[#5C625F]">{displayUser.email}</p>
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              <Badge className="rounded-full bg-[#F3EFE9] font-mono text-[#2A4B46]">{displayUser.id}</Badge>
              <Badge className="rounded-full bg-[#D4A373] text-white">{levelLabel}</Badge>
            </div>
          </div>

          {displayUser.role === "partner" && (
            <div className="mt-6 rounded-2xl border border-[#E6E4E0] bg-[#FCFBF8] p-4">
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm font-semibold text-[#2A4B46]">Votre Progression de niveau</span>
                <span className="text-xs font-medium text-[#5C625F]">{progress.label}</span>
              </div>
              <Progress value={progress.percent} className="mt-3 h-2 bg-[#E6E4E0] [&>div]:bg-[#D4A373]" />
            </div>
          )}

          {!displayUser.is_verified && displayUser.role === "partner" && (
            <Link
              to="/verification"
              onClick={() => setOpen(false)}
              className="mt-4 flex items-center justify-center gap-2 rounded-full bg-orange-100 px-4 py-2.5 text-sm font-semibold text-orange-800 transition-colors hover:bg-orange-200"
            >
              <AlertTriangle size={17} /> Verifier mon identite
            </Link>
          )}
        </div>

        <div className="mt-5 space-y-2">
          {isFullAdmin && (
            <DrawerAction to="/admin/users" icon={Settings} onClick={() => setOpen(false)} highlight>
              Acceder au Hub Gestion
            </DrawerAction>
          )}
          <DrawerAction to="/profile" icon={UserCog} onClick={() => setOpen(false)}>
            Gerer mon compte
          </DrawerAction>
          <DrawerAction to="/forgot-password" icon={KeyRound} onClick={() => setOpen(false)}>
            Modifier le mot de passe
          </DrawerAction>
          <DrawerAction to="/documents" icon={Download} onClick={() => setOpen(false)}>
            Mes documents
          </DrawerAction>
        </div>

        <Button
          variant="outline"
          className="mt-6 w-full justify-center gap-3 rounded-full border-[#E6E4E0] text-red-700 hover:bg-red-50"
          onClick={onLogout}
        >
          <LogOut size={18} /> Deconnexion
        </Button>
      </SheetContent>
    </Sheet>
  );
}

function DrawerAction({ to, icon: Icon, children, onClick, highlight = false }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className={
        highlight
          ? "flex items-center gap-3 rounded-2xl bg-[#2A4B46] px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#1f3a36]"
          : "flex items-center gap-3 rounded-2xl border border-[#E6E4E0] px-4 py-3 text-sm font-medium text-[#2A4B46] transition-colors hover:bg-[#F3EFE9]"
      }
    >
      <Icon size={18} /> {children}
    </Link>
  );
}
